import { useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'

const ENTRY_KEY = 'caiCloseOnBack'

// Set while a back() this hook issued is in flight; a push made before it lands gets swept up in it.
let pendingPop: Promise<void> | null = null

function popEntry() {
  pendingPop = new Promise((resolve) => {
    window.addEventListener(
      'popstate',
      () => {
        pendingPop = null
        resolve()
      },
      { once: true }
    )
  })
  window.history.back()
}

/**
 * Lets the back button — Android's back gesture included — close an overlay instead of
 * leaving the page. Opening adds a same-URL history entry for back to use up.
 */
export function useCloseOnBack(open: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const entryId = uuidv4()
    let cancelled = false
    let pushed = false
    let usedUpByBack = false
    function onPopState() {
      usedUpByBack = true
      onCloseRef.current()
    }
    function push() {
      if (cancelled) return
      // Spread, not replace: React Router reads its own index off whichever entry it pops to.
      window.history.pushState({ ...window.history.state, [ENTRY_KEY]: entryId }, '')
      pushed = true
      window.addEventListener('popstate', onPopState)
    }
    if (pendingPop) void pendingPop.then(push)
    else push()
    return () => {
      cancelled = true
      window.removeEventListener('popstate', onPopState)
      // Only while our entry is still current: a navigation made while open must not be undone.
      if (pushed && !usedUpByBack && window.history.state?.[ENTRY_KEY] === entryId) popEntry()
    }
  }, [open])
}
